import {castArray, get} from 'lodash';
import {SupportedLocale} from '@/initializers/i18next';
import {logger} from '@/initializers/logger';
import {
  ValueDefinition,
  DomainValueReference,
  BaseValue,
  CustomValue,
  LocalizedString,
  TemplateValue,
  DomainValueListReference,
  Value,
  Option,
} from '@/model/types';
import {FilterFn, getFilterFn} from '../filters';
import {ValueResolver, ValueResolverOptions} from '../types';
import {resolveTemplate} from './template';

const findActiveValueByIdBeneva = (expiredValue: CustomValue, definition: ValueDefinition): CustomValue | undefined => {
  const idBeneva = expiredValue.properties.id_beneva as string | undefined;
  if (!idBeneva) {
    return undefined;
  }

  const today = new Date();

  for (const v of definition.values) {
    if (!('properties' in v)) {
      continue;
    }

    const cv = v as CustomValue;
    if (cv.id === expiredValue.id) {
      continue;
    }
    if ((cv.properties.id_beneva as string | undefined) !== idBeneva) {
      continue;
    }

    const dtDb = cv.properties.DtDb as string | undefined;
    const dtEx = cv.properties.DtEx as string | undefined;
    if (!dtDb || !dtEx) {
      continue;
    }

    if (new Date(dtDb) <= today && new Date(dtEx) >= today) {
      return cv;
    }
  }

  return undefined;
};

export const lookupValueDomain = <T = LocalizedString>(
  domain: ValueDefinition[],
  valueRef: DomainValueReference,
  valueResolver: ValueResolver,
  options: ValueResolverOptions<T>,
  locale: SupportedLocale,
): LocalizedString | TemplateValue | T => {
  const [ref, id] = valueRef.$ref.split('#');
  const definition = domain.find(definition => definition.id === ref);
  if (!definition) throw new Error(`Value not found in domain: ${valueRef.$ref}`);
  const {property, transform} = valueRef;

  if (property) {
    if (property === 'acronym' || 'type' in definition) {
      const value = definition?.values.find(value => value.id === id);
      if (!value) throw new Error(`Value not found in domain: ${valueRef.$ref}`);

      const propertyPath =
        typeof property === 'string'
          ? property
          : typeof property.$template === 'string'
            ? resolveTemplate(property.$template, valueResolver, options, locale)
            : resolveTemplate(property.$template[locale], valueResolver, options, locale);

      let propertyValue = undefined;
      if (propertyPath === 'acronym') {
        propertyValue = value.acronym;
      } else if ('type' in definition) {
        propertyValue = get((value as CustomValue).properties, propertyPath);
      }

      if (propertyValue === undefined) {
        propertyValue = '';
        logger.warn(`Property '${propertyPath}' not found in value: ${valueRef.$ref}`);
      }

      // Normalize boolean values to strings for consistent handling
      if (typeof propertyValue === 'boolean') {
        propertyValue = String(propertyValue);
      }

      if (transform) {
        return applyDomainTransform(propertyValue, transform) as T;
      }
      return propertyValue as T;
    }
    throw new Error(`Value '${valueRef.$ref}' is not a custom value`);
  }

  const value = definition.values.find(value => value.id === id);
  if (!value) throw new Error(`Value not found in domain: ${valueRef.$ref}`);

  if ('properties' in value) {
    const dtEx = (value as CustomValue).properties.DtEx as string | undefined;
    if (dtEx && new Date(dtEx) < new Date()) {
      const activeReplacement = findActiveValueByIdBeneva(value as CustomValue, definition);
      if (activeReplacement) {
        return activeReplacement.value;
      }
    }
  }

  return value.value;
};

export const applyDomainTransform = (value: unknown, transform: string): string => {
  switch (transform) {
    case 'count':
      if (value === undefined) return '0';
      return castArray(value).length.toFixed();
    default:
      throw new Error(`Unsupported transform: ${transform}`);
  }
};

export const mapValueDomainList = (
  domain: ValueDefinition[],
  valueRef: DomainValueListReference,
  valueResolver: ValueResolver,
): Option[] | DomainValueReference[] => {
  const {$ref, sort, filters, property, asOption, ...otherModifiers} = valueRef;

  const definition = domain.find(definition => definition.id === $ref);
  if (!definition) throw new Error(`Value not found in domain: ${$ref}`);

  let sortFn: (a: Value, b: Value) => number = () => 0; // Sort noop
  if (sort === 'asc') {
    sortFn = (a, b) => valueResolver(a.value).localeCompare(valueResolver(b.value));
  } else if (sort === 'desc') {
    sortFn = (a, b) => valueResolver(b.value).localeCompare(valueResolver(a.value));
  }

  const filterFn = (filters ?? []).reduce<FilterFn>(
    (acc, filter) => {
      const fn = getFilterFn(filter)(valueResolver);
      return (value, definition) => acc(value, definition) && fn(value, definition);
    },
    () => true,
  );

  const baseValues: BaseValue[] = definition.values
    .slice()
    .filter(value => filterFn(value, definition))
    .sort(sortFn);

  if (asOption) {
    const mapperFn = (value: BaseValue | CustomValue): Option => ({
      label: {$ref: `${$ref}#${value.id}`},
      value: {
        ...otherModifiers,
        property,
        $ref: `${$ref}#${value.id}`,
      },
    });
    return baseValues.map(mapperFn);
  } else {
    const mapperFn = (value: BaseValue | CustomValue): DomainValueReference => ({
      ...otherModifiers,
      ...(property ? {property} : {}),
      $ref: `${$ref}#${value.id}`,
    });

    return baseValues.map(mapperFn);
  }
};
